import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import {
  createPage,
  deletePage,
  getPage,
  listPages,
  PAGES_BASE,
  publishPage,
  unpublishPage,
  updatePage,
  type PageListQuery,
} from './pages.api';

import type { UpdateWebsitePageRequest } from '@utazzo/shared';

const KEY = 'website-pages';

/**
 * All hooks accept an optional `basePath`. The default (`/admin/website/pages`)
 * is Custom Pages; the Tourism Boards / Landing Pages screens pass their own
 * type-scoped endpoint. `basePath` is part of the query key so the three
 * screens keep independent caches.
 */
export const usePagesList = (query: PageListQuery, basePath: string = PAGES_BASE) =>
  useQuery({
    queryKey: [KEY, 'list', basePath, query],
    queryFn: () => listPages(query, basePath),
    staleTime: 10_000,
  });

export const usePage = (id: string | null, basePath: string = PAGES_BASE) =>
  useQuery({
    queryKey: [KEY, 'detail', basePath, id],
    queryFn: () => getPage(id as string, basePath),
    enabled: !!id,
  });

export const useCreatePage = (basePath: string = PAGES_BASE) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Parameters<typeof createPage>[0]) => createPage(payload, basePath),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [KEY] });
      toast.success('Page created');
    },
  });
};

export const useUpdatePage = (basePath: string = PAGES_BASE) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateWebsitePageRequest }) =>
      updatePage(id, payload, basePath),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [KEY] });
      toast.success('Page saved');
    },
  });
};

export const usePublishPage = (basePath: string = PAGES_BASE) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => publishPage(id, basePath),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [KEY] });
      toast.success('Page published');
    },
  });
};

export const useUnpublishPage = (basePath: string = PAGES_BASE) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => unpublishPage(id, basePath),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [KEY] });
      toast.success('Page unpublished');
    },
  });
};

export const useDeletePage = (basePath: string = PAGES_BASE) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deletePage(id, basePath),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [KEY] });
      toast.success('Page deleted');
    },
  });
};
